import { useEffect, useState } from 'react';
import { Button, Field, Notice } from '../../components/ui';
import { Dialog } from '../../components/Dialog';
import { SearchAdd, type SearchAddItem } from '../../components/SearchAdd';
import { api, ApiError } from '../../lib/api';

/** Organisations to move someone to, searched on the server so it copes with thousands. */
async function searchOrganisations(q: string, leaveOut: string | null): Promise<SearchAddItem[]> {
  const res = await api<{ data: { id: string; name: string; type?: string; invite_code: string | null; members: number }[] }>(
    `/admin/schools?q=${encodeURIComponent(q)}&page=1&pageSize=12`,
  );
  return res.data
    .filter((o) => o.id !== leaveOut)
    .map((o) => ({
      id: o.id,
      title: o.name,
      sub: [o.type === 'personal' ? 'Personal' : null, o.invite_code, `${Number(o.members)} ${Number(o.members) === 1 ? 'person' : 'people'}`]
        .filter(Boolean)
        .join(' · '),
    }));
}

/** Anyone an admin can act on, from an organisation's people or from search. */
export interface AdminPerson {
  id: string;
  name: string;
  email: string;
  role: string;
  isOwner: boolean;
  schoolId: string | null;
  schoolName: string | null;
  /** Taken out of this organisation and paused until they're added to one. */
  removedFrom?: string | null;
}

type Kind = 'reset' | 'role' | 'owner' | 'move' | 'personal' | 'remove' | 'delete';

const ROLE: Record<string, string> = { teacher: 'Teacher', school_leader: 'Leader', system_admin: 'EducAitor admin' };

/**
 * Everything an admin can do to one person: send a password reset, change
 * their role or ownership, move them to another organisation, or take them
 * out of theirs. Pick an action, then confirm it. Every change is recorded
 * in the activity log.
 */
export function ManagePerson({
  person,
  onClose,
  onDone,
}: {
  person: AdminPerson | null;
  onClose: () => void;
  /** After a change, with a line to show. */
  onDone: (message: string) => void;
}) {
  const [kind, setKind] = useState<Kind | null>(null);
  const [target, setTarget] = useState<SearchAddItem | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setKind(null);
    setTarget(null);
    setTyped('');
    setError(null);
  }, [person?.id]);

  if (!person) return null;
  const p = person;
  const admin = p.role === 'system_admin';
  const org = p.schoolName ?? 'their organisation';

  const actions: { kind: Kind; label: string; hint: string; show: boolean }[] = [
    { kind: 'reset', label: 'Send a password reset', hint: `The usual 6-digit code, to ${p.email}. You never see or set their password.`, show: true },
    {
      kind: 'role',
      label: p.role === 'teacher' ? 'Make them a leader' : 'Make them a teacher',
      hint: p.role === 'teacher' ? 'They get the Leadership Hub and managing people.' : 'They lose leader access and ownership.',
      show: !!p.schoolId,
    },
    {
      kind: 'owner',
      label: p.isOwner ? 'Remove as owner' : 'Make them an owner',
      hint: p.isOwner ? 'Make sure the organisation keeps another owner.' : 'Owners finish setup, manage seats and invite leaders.',
      show: !!p.schoolId && p.role === 'school_leader',
    },
    {
      kind: 'move',
      label: p.schoolId ? 'Move to another organisation' : 'Add to an organisation',
      hint: p.removedFrom
        ? 'Lets them back in, with their account and lessons. To use EducAitor on their own instead, give them a personal account.'
        : 'Their account and private lessons go with them. Ownership is cleared.',
      show: true,
    },
    {
      kind: 'personal',
      label: 'Give them a personal account',
      hint: 'Their own one-seat account, for using EducAitor on their own. They keep their lessons, carry on as a teacher in the app and can record again straight away.',
      show: !p.schoolId,
    },
    {
      kind: 'remove',
      label: `Remove from ${org}`,
      hint: 'Their account is paused until they’re added to an organisation. They keep their account and lessons, and their lessons stop counting for this one.',
      show: !!p.schoolId,
    },
    {
      kind: 'delete',
      label: 'Delete their account',
      hint: 'Their account, lessons, reports and messages, for good. For a request to delete their data.',
      show: true,
    },
  ];

  const run = async () => {
    if (!kind) return;
    setBusy(true);
    setError(null);
    try {
      if (kind === 'reset') await api(`/admin/users/${p.id}/password-reset`, { method: 'POST', body: '{}' });
      if (kind === 'role')
        await api(`/admin/users/${p.id}`, { method: 'PATCH', body: JSON.stringify({ role: p.role === 'teacher' ? 'school_leader' : 'teacher' }) });
      if (kind === 'owner') await api(`/admin/users/${p.id}`, { method: 'PATCH', body: JSON.stringify({ isOwner: !p.isOwner }) });
      if (kind === 'move') await api(`/admin/users/${p.id}`, { method: 'PATCH', body: JSON.stringify({ schoolId: target?.id }) });
      if (kind === 'remove') await api(`/admin/users/${p.id}`, { method: 'PATCH', body: JSON.stringify({ schoolId: null }) });
      if (kind === 'personal') await api(`/admin/users/${p.id}/personal-account`, { method: 'POST', body: '{}' });
      let signInLeft = false;
      if (kind === 'delete') signInLeft = (await api<{ signInsLeft: string[] }>(`/admin/users/${p.id}`, { method: 'DELETE' })).signInsLeft.length > 0;
      const to = target?.title;
      onDone(
        kind === 'reset'
          ? `A password reset code is on its way to ${p.email}.`
          : kind === 'move'
            ? `${p.name} moved to ${to ?? 'their new organisation'}.`
            : kind === 'personal'
              ? `${p.name} has a personal account and can record again.`
              : kind === 'remove'
              ? `${p.name} removed from ${org}. Their account is paused.`
              : kind === 'delete'
                ? `${p.name}’s account deleted.${signInLeft ? ` Their sign-in (${p.email}) needs removing in AWS.` : ''}`
                : `${p.name} updated.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const chosen = actions.find((a) => a.kind === kind);
  return (
    <Dialog open onClose={() => !busy && onClose()} title={p.name}>
      <p className="muted manage-who">
        {p.email} · {ROLE[p.role] ?? p.role}
        {p.isOwner ? ', owner' : ''} · {p.schoolName ?? (p.removedFrom ? `Removed from ${p.removedFrom}, paused` : 'No organisation')}
      </p>
      {admin ? (
        <Notice>EducAitor admins are changed directly in the database.</Notice>
      ) : !chosen ? (
        <div className="manage-actions">
          {actions
            .filter((a) => a.show)
            .map((a) => (
              <button
                key={a.kind}
                type="button"
                className="manage-action"
                data-danger={a.kind === 'remove' || a.kind === 'delete' || undefined}
                onClick={() => setKind(a.kind)}
              >
                <span>{a.label}</span>
                <span className="manage-hint">{a.hint}</span>
              </button>
            ))}
        </div>
      ) : (
        <>
          <p>
            <strong>{chosen.label}</strong>
          </p>
          <p className="muted">{chosen.hint}</p>
          {kind === 'move' ? (
            target ? (
              <div className="field">
                <span className="field-label">Organisation</span>
                <div className="picked-row">
                  <span className="search-add-text">
                    <span className="search-add-title">{target.title}</span>
                    {target.sub ? <span className="search-add-sub">{target.sub}</span> : null}
                  </span>
                  <Button variant="ghost" onClick={() => setTarget(null)} disabled={busy}>
                    Change
                  </Button>
                </div>
              </div>
            ) : (
              <SearchAdd
                label="Organisation"
                placeholder="Search by name, code or trust"
                noun="organisation"
                actionLabel="Choose"
                busyLabel="Choose"
                autoFocus
                inline
                load={(q) => searchOrganisations(q, p.schoolId)}
                onAdd={async (o) => setTarget(o)}
              />
            )
          ) : null}
          {kind === 'delete' ? (
            <Field
              id="manage-delete-confirm"
              label={`Type ${p.email} to confirm`}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
            />
          ) : null}
          {error ? <Notice tone="error">{error}</Notice> : null}
          <div className="dialog-actions">
            <Button variant="ghost" onClick={() => setKind(null)} disabled={busy}>
              Back
            </Button>
            <Button
              variant={kind === 'remove' || kind === 'delete' ? 'danger' : 'primary'}
              onClick={run}
              loading={busy}
              disabled={(kind === 'move' && !target) || (kind === 'delete' && typed.trim().toLowerCase() !== p.email.toLowerCase())}
            >
              {kind === 'reset' ? 'Send' : kind === 'delete' ? 'Delete for good' : 'Confirm'}
            </Button>
          </div>
        </>
      )}
      {!chosen ? (
        <div className="dialog-actions">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      ) : null}
    </Dialog>
  );
}
